import {
  Button,
  Card,
  FileButton,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import type {
  selectPropertyWithAddressSchema,
  WarrantyMetadata,
} from '@markku/types'
import { IconPlus, IconShield } from '@tabler/icons-react'
import { z } from 'zod'
import type { AttachmentHooks } from '../attachments/hooks'
import { AddPhotoButton } from '../common/AddPhotoButton'
import { StreetViewCard } from '../StreetViewCard'
import { CategoryAttachments } from './CategoryAttachments'
import { EditableSection, FieldConfig } from './EditableSection'
import type { PropertyHooks } from './hooks'
import { useAttachmentUpload } from './useAttachmentUpload'
import { WarrantyList } from './WarrantyList'
import { WarrantyModal } from './WarrantyModal'

interface HomeDetailsProps {
  property: z.infer<typeof selectPropertyWithAddressSchema>
  propertyHooks: PropertyHooks
  attachmentHooks: AttachmentHooks
  canEdit?: boolean
}

export function HomeDetails({
  property,
  propertyHooks,
  attachmentHooks,
  canEdit = true,
}: HomeDetailsProps) {
  const [
    warrantyModalOpened,
    { open: openWarrantyModal, close: closeWarrantyModal },
  ] = useDisclosure(false)

  const { mutateAsync: updateProperty, isPending: isUpdating } =
    propertyHooks.useUpdate()

  const handleUpdate = async (values: Record<string, unknown>) => {
    // Convert empty strings to null for proper validation
    const cleaned = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, v === '' ? null : v]),
    )

    await updateProperty({
      id: property.id,
      data: {
        ...cleaned,
        data: {
          ...(property.data as Record<string, unknown>),
          ...(cleaned.data as Record<string, unknown>),
        },
        address: {
          ...property.address,
          ...(cleaned.address as Record<string, unknown>),
        },
      },
    })
  }

  // Upload hooks for all sections
  const generalUpload = useAttachmentUpload({
    category: 'general',
    propertyId: property.id,
    propertyHooks,
    attachmentHooks,
  })

  const locationUpload = useAttachmentUpload({
    category: 'location',
    propertyId: property.id,
    propertyHooks,
    attachmentHooks,
  })

  const buildingUpload = useAttachmentUpload({
    category: 'building',
    propertyId: property.id,
    propertyHooks,
    attachmentHooks,
  })

  const systemsUpload = useAttachmentUpload({
    category: 'systems',
    propertyId: property.id,
    propertyHooks,
    attachmentHooks,
  })

  const roofUpload = useAttachmentUpload({
    category: 'roof',
    propertyId: property.id,
    propertyHooks,
    attachmentHooks,
  })

  const exteriorUpload = useAttachmentUpload({
    category: 'exterior',
    propertyId: property.id,
    propertyHooks,
    attachmentHooks,
  })

  const documentsUpload = useAttachmentUpload({
    category: 'documents',
    propertyId: property.id,
    allowDocuments: true,
    propertyHooks,
    attachmentHooks,
  })

  const warrantyUpload = useAttachmentUpload({
    category: 'warranty',
    propertyId: property.id,
    allowDocuments: true,
    successMessage: 'Takuutodistus tallennettiin onnistuneesti',
    propertyHooks,
    attachmentHooks,
  })

  const handleWarrantySubmit = async (data: {
    file: File
    metadata: Omit<WarrantyMetadata, 'category'>
  }) => {
    await warrantyUpload.handleUpload(data.file, data.metadata)
    closeWarrantyModal()
  }

  const handleWarrantyDelete = async (attachmentId: number) => {
    await updateProperty({
      id: property.id,
      data: {},
      attachments: [{ id: attachmentId, action: 'delete' }],
    })
  }

  const locationFields: FieldConfig[] = [
    { key: 'address.street', label: 'Katuosoite', type: 'text' },
    { key: 'address.postalCode', label: 'Postinumero', type: 'text' },
    { key: 'address.city', label: 'Kaupunki', type: 'text' },
    { key: 'data.propertyId', label: 'Kiinteistötunnus', type: 'text' },
    {
      key: 'data.access',
      label: 'Pääsy kohteeseen',
      type: 'text',
      visibilityRoles: ['employee'],
    },
  ]

  const buildingFields: FieldConfig[] = [
    { key: 'constructionYear', label: 'Rakennusvuosi', type: 'number' },
    { key: 'data.floorArea', label: 'Kokonaisala', type: 'number', unit: 'm²' },
    { key: 'data.livingArea', label: 'Asuinala', type: 'number', unit: 'm²' },
    { key: 'data.numberOfFloors', label: 'Kerrosten määrä', type: 'number' },
    { key: 'data.buildingMaterial', label: 'Rakennusmateriaali', type: 'text' },
    { key: 'data.residentCount', label: 'Asukasmäärä', type: 'number' },
  ]

  const roofFields: FieldConfig[] = [
    { key: 'data.roofType', label: 'Kattotyyppi', type: 'text' },
    { key: 'data.roofMaterial', label: 'Kattomateriaali', type: 'text' },
    { key: 'data.roofSize', label: 'Katon koko', type: 'text' },
    {
      key: 'data.roofPenetration',
      label: 'Läpiviennit ja hormit',
      type: 'text',
    },
    { key: 'data.roofSafety', label: 'Kattoturvatuotteet', type: 'text' },
    { key: 'data.gutterLength', label: 'Räystäskourujen pituus', type: 'text' },
  ]

  const systemsFields: FieldConfig[] = [
    { key: 'data.heating', label: 'Lämmitysjärjestelmä', type: 'text' },
    { key: 'data.heatPump', label: 'Lämpöpumppu', type: 'text' },
    { key: 'data.ventilation', label: 'Ilmanvaihto', type: 'text' },
    { key: 'data.waterAndSewer', label: 'Vesi ja viemäri', type: 'text' },
    { key: 'data.saunaStove', label: 'Kiuas', type: 'text' },
    { key: 'data.cookerHood', label: 'Liesituuletin', type: 'text' },
    { key: 'data.centralVacuum', label: 'Keskuspölynimuri', type: 'text' },
    { key: 'data.tvConnection', label: 'TV-liittymä', type: 'text' },
    {
      key: 'data.mainWaterValveLocation',
      label: 'Pääsulun sijainti',
      type: 'text',
    },
    { key: 'data.fireplaces', label: 'Tulisijat', type: 'text' },
    {
      key: 'data.fireAlarms',
      label: 'Palovaroittimet',
      type: 'number',
      formatDisplay: (value) => (value ? `${value} kpl` : null),
    },
  ]

  const exteriorFields: FieldConfig[] = [
    {
      key: 'data.lotArea',
      label: 'Tontin pinta-ala',
      type: 'number',
      unit: 'm²',
    },
    { key: 'data.lawnArea', label: 'Nurmikon pinta-ala', type: 'text' },
    {
      key: 'data.drainageWells',
      label: 'Sadevesikaivot',
      type: 'number',
      formatDisplay: (value) => (value ? `${value} kpl` : null),
    },
  ]

  return (
    <Stack gap="lg">
      {/* General photos - overview, exterior */}
      <Card>
        <Group justify="space-between" mb="md">
          <Title order={4}>Yleiskuvat</Title>
          {canEdit && (
            <AddPhotoButton
              onFileSelect={generalUpload.handleUpload}
              loading={generalUpload.uploading}
            />
          )}
        </Group>
        <Text size="sm" c="dimmed" mb="md">
          Kiinteistön ulkokuvia ja yleiskuvia
        </Text>
        <Stack gap="md">
          {property.address && (
            <StreetViewCard
              address={property.address}
              apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}
            />
          )}
          <CategoryAttachments
            category="general"
            attachments={property.attachments}
            canEdit={canEdit}
            propertyId={property.id}
            propertyHooks={propertyHooks}
            attachmentHooks={attachmentHooks}
            showInlineButton={false}
          />
        </Stack>
      </Card>

      <EditableSection
        title="Sijainti"
        fields={locationFields}
        entity={property}
        onUpdate={handleUpdate}
        isUpdating={isUpdating}
        canEdit={canEdit}
        attachmentSlot={
          <CategoryAttachments
            category="location"
            attachments={property.attachments}
            canEdit={canEdit}
            propertyId={property.id}
            propertyHooks={propertyHooks}
            attachmentHooks={attachmentHooks}
            compact
            showInlineButton={false}
          />
        }
        attachmentAddButton={
          canEdit && (
            <AddPhotoButton
              onFileSelect={locationUpload.handleUpload}
              loading={locationUpload.uploading}
            />
          )
        }
      />

      <EditableSection
        title="Rakennuksen tiedot"
        fields={buildingFields}
        entity={property}
        onUpdate={handleUpdate}
        isUpdating={isUpdating}
        canEdit={canEdit}
        attachmentSlot={
          <CategoryAttachments
            category="building"
            attachments={property.attachments}
            canEdit={canEdit}
            propertyId={property.id}
            propertyHooks={propertyHooks}
            attachmentHooks={attachmentHooks}
            compact
            showInlineButton={false}
          />
        }
        attachmentAddButton={
          canEdit && (
            <AddPhotoButton
              onFileSelect={buildingUpload.handleUpload}
              loading={buildingUpload.uploading}
            />
          )
        }
      />

      <EditableSection
        title="Katto ja räystäät"
        fields={roofFields}
        entity={property}
        onUpdate={handleUpdate}
        isUpdating={isUpdating}
        canEdit={canEdit}
        attachmentSlot={
          <CategoryAttachments
            category="roof"
            attachments={property.attachments}
            canEdit={canEdit}
            propertyId={property.id}
            propertyHooks={propertyHooks}
            attachmentHooks={attachmentHooks}
            compact
            showInlineButton={false}
          />
        }
        attachmentAddButton={
          canEdit && (
            <AddPhotoButton
              onFileSelect={roofUpload.handleUpload}
              loading={roofUpload.uploading}
            />
          )
        }
      />

      <EditableSection
        title="Tekniset järjestelmät"
        fields={systemsFields}
        entity={property}
        onUpdate={handleUpdate}
        isUpdating={isUpdating}
        canEdit={canEdit}
        attachmentSlot={
          <CategoryAttachments
            category="systems"
            attachments={property.attachments}
            canEdit={canEdit}
            propertyId={property.id}
            propertyHooks={propertyHooks}
            attachmentHooks={attachmentHooks}
            compact
            showInlineButton={false}
          />
        }
        attachmentAddButton={
          canEdit && (
            <AddPhotoButton
              onFileSelect={systemsUpload.handleUpload}
              loading={systemsUpload.uploading}
            />
          )
        }
      />

      <EditableSection
        title="Ulkoalueet ja piha"
        fields={exteriorFields}
        entity={property}
        onUpdate={handleUpdate}
        isUpdating={isUpdating}
        canEdit={canEdit}
        attachmentSlot={
          <CategoryAttachments
            category="exterior"
            attachments={property.attachments}
            canEdit={canEdit}
            propertyId={property.id}
            propertyHooks={propertyHooks}
            attachmentHooks={attachmentHooks}
            compact
            showInlineButton={false}
          />
        }
        attachmentAddButton={
          canEdit && (
            <AddPhotoButton
              onFileSelect={exteriorUpload.handleUpload}
              loading={exteriorUpload.uploading}
            />
          )
        }
      />

      {/* Documents section */}
      <Card id="documents">
        <Group justify="space-between" mb="md">
          <Title order={4}>Dokumentit</Title>
          {canEdit && (
            <FileButton
              onChange={(file) => file && documentsUpload.handleUpload(file)}
            >
              {(props) => (
                <Button
                  {...props}
                  variant="outline"
                  size="xs"
                  leftSection={<IconPlus size={14} />}
                  loading={documentsUpload.uploading}
                >
                  Lisää tiedosto
                </Button>
              )}
            </FileButton>
          )}
        </Group>
        <Text size="sm" c="dimmed" mb="md">
          Todistukset ja muut dokumentit
        </Text>
        <CategoryAttachments
          category="documents"
          attachments={property.attachments}
          canEdit={canEdit}
          propertyId={property.id}
          propertyHooks={propertyHooks}
          attachmentHooks={attachmentHooks}
          allowDocuments
          showInlineButton={false}
        />
      </Card>

      {/* Warranties section */}
      <Card id="warranties">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconShield size={20} color="var(--mantine-color-green-6)" />
            <Title order={4}>Takuut</Title>
          </Group>
          {canEdit && (
            <Button
              variant="outline"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={openWarrantyModal}
            >
              Lisää takuu
            </Button>
          )}
        </Group>
        <WarrantyList
          attachments={property.attachments || []}
          onDelete={canEdit ? handleWarrantyDelete : undefined}
          canEdit={canEdit}
        />
      </Card>

      {/* Warranty Modal */}
      <WarrantyModal
        opened={warrantyModalOpened}
        onClose={closeWarrantyModal}
        onSubmit={handleWarrantySubmit}
        loading={warrantyUpload.uploading}
      />
    </Stack>
  )
}
